import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Plus, Pencil, Trash2, MessageCircle, X, Search } from "lucide-react";
import { toast } from "sonner";

const ESTADOS = ["Pendente", "Realizada", "Cancelada"];

const EMPTY = {
  condominio_id: "",
  empresa_condominio_id: "",
  contacto_nome: "",
  contacto_telefone: "",
  contacto_email: "",
  assunto: "",
  descricao: "",
  data: new Date().toISOString().slice(0, 10),
  estado: "Pendente",
};

export default function Consultas() {
  const [items, setItems] = useState([]);
  const [condominios, setCondominios] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        api.get("/consultas"),
        api.get("/condominios"),
        api.get("/empresas-condominio"),
      ]);
      setItems(a.data);
      setCondominios(b.data);
      setEmpresas(c.data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  function openCreate() {
    setEditing(null);
    setForm(EMPTY);
    setOpenForm(true);
  }

  function openEdit(item) {
    setEditing(item);
    setForm({ ...EMPTY, ...item });
    setOpenForm(true);
  }

  async function onSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        condominio_id: form.condominio_id || null,
        empresa_condominio_id: form.empresa_condominio_id || null,
      };
      if (editing) {
        await api.put(`/consultas/${editing.id}`, payload);
        toast.success("Consulta atualizada");
      } else {
        await api.post("/consultas", payload);
        toast.success("Consulta registada");
      }
      setOpenForm(false);
      refresh();
    } catch (er) {
      toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message);
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(item) {
    if (!window.confirm(`Eliminar consulta "${item.assunto}"?`)) return;
    try {
      await api.delete(`/consultas/${item.id}`);
      toast.success("Consulta eliminada");
      refresh();
    } catch (er) {
      toast.error(formatApiErrorDetail(er.response?.data?.detail) || er.message);
    }
  }

  const filtered = items.filter((i) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (i.assunto || "").toLowerCase().includes(q) ||
      (i.contacto_nome || "").toLowerCase().includes(q) ||
      (i.condominio_nome || "").toLowerCase().includes(q) ||
      (i.empresa_condominio_nome || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6" data-testid="consultas-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
          <input
            data-testid="consultas-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Pesquisar por assunto, contacto, condomínio ou empresa…"
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-zinc-200 rounded-sm text-sm font-manrope focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none"
          />
        </div>
        <button
          data-testid="consultas-new-btn"
          onClick={openCreate}
          className="bg-[#0047FF] text-white hover:bg-[#003BCC] rounded-sm px-5 py-2.5 font-manrope font-semibold text-sm transition-colors flex items-center gap-2"
        >
          <Plus className="w-4 h-4" strokeWidth={2} />
          Nova consulta
        </button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-sm font-manrope text-neutral-500">A carregar…</div>
        ) : filtered.length === 0 ? (
          <div className="p-14 text-center">
            <MessageCircle className="w-8 h-8 text-neutral-300 mx-auto mb-4" strokeWidth={1.5} />
            <div className="text-sm font-manrope text-neutral-500">
              {items.length === 0 ? "Sem consultas registadas." : "Sem resultados."}
            </div>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-zinc-50/50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold">
                <th className="py-3 px-6 text-left">Data</th>
                <th className="py-3 px-6 text-left">Assunto</th>
                <th className="py-3 px-6 text-left">Contacto</th>
                <th className="py-3 px-6 text-left">Ligação</th>
                <th className="py-3 px-6 text-left">Estado</th>
                <th className="py-3 px-6 text-left">Registado por</th>
                <th className="py-3 px-6 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} data-testid={`consulta-row-${c.id}`} className="border-b border-zinc-100 hover:bg-zinc-50/60">
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-700 font-mono">{c.data}</td>
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-950 font-semibold">{c.assunto}</td>
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-700">
                    <div>{c.contacto_nome}</div>
                    {c.contacto_email && <div className="text-xs text-neutral-500">{c.contacto_email}</div>}
                    {c.contacto_telefone && <div className="text-xs text-neutral-500">{c.contacto_telefone}</div>}
                  </td>
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-600">
                    {c.condominio_nome && <div>Cond.: {c.condominio_nome}</div>}
                    {c.empresa_condominio_nome && <div className="text-xs text-neutral-500">Emp.: {c.empresa_condominio_nome}</div>}
                    {!c.condominio_nome && !c.empresa_condominio_nome && <span className="text-neutral-400">—</span>}
                  </td>
                  <td className="py-4 px-6"><EstadoChip s={c.estado} /></td>
                  <td className="py-4 px-6 font-manrope text-sm text-neutral-700">{c.created_by_name || "—"}{c.created_by_email && <div className="text-xs text-neutral-500 font-mono">{c.created_by_email}</div>}</td>
                  <td className="py-4 px-6 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button data-testid={`edit-consulta-${c.id}`} onClick={() => openEdit(c)} className="p-2 text-neutral-500 hover:text-[#0047FF] hover:bg-[#0047FF]/5 rounded-sm">
                        <Pencil className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                      <button data-testid={`delete-consulta-${c.id}`} onClick={() => onDelete(c)} className="p-2 text-neutral-500 hover:text-[#FF4500] hover:bg-[#FF4500]/5 rounded-sm">
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {openForm && (
        <div className="fixed inset-0 bg-neutral-950/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setOpenForm(false)}>
          <div className="bg-white rounded-sm w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="consultas-modal">
            <div className="sticky top-0 bg-white border-b border-zinc-200 px-8 py-5 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-400 font-chivo font-bold mb-1">
                  {editing ? "Editar" : "Novo registo"}
                </div>
                <h2 className="text-xl font-chivo font-black text-neutral-950 tracking-tight">
                  {editing ? "Editar consulta" : "Nova consulta"}
                </h2>
              </div>
              <button onClick={() => setOpenForm(false)} className="p-2 text-neutral-400 hover:text-neutral-900 rounded-sm hover:bg-zinc-100">
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>
            <form onSubmit={onSubmit} className="p-8 space-y-5" data-testid="consultas-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <TextField label="Data *" testid="input-data" type="date" required value={form.data} onChange={(v) => setForm({ ...form, data: v })} />
                <SelectField label="Estado *" testid="input-estado" value={form.estado} onChange={(v) => setForm({ ...form, estado: v })} options={ESTADOS.map((e) => ({ value: e, label: e }))} />
              </div>
              <TextField label="Assunto *" testid="input-assunto" required value={form.assunto} onChange={(v) => setForm({ ...form, assunto: v })} placeholder="Ex.: Pedido de orçamento para reabilitação" />
              <div className="border-t border-zinc-200 pt-5">
                <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500 font-chivo font-bold mb-4">Contacto</div>
                <TextField label="Nome *" testid="input-contacto-nome" required value={form.contacto_nome} onChange={(v) => setForm({ ...form, contacto_nome: v })} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
                  <TextField label="Telefone" testid="input-contacto-tel" value={form.contacto_telefone} onChange={(v) => setForm({ ...form, contacto_telefone: v })} />
                  <TextField label="Email" type="email" testid="input-contacto-email" value={form.contacto_email} onChange={(v) => setForm({ ...form, contacto_email: v })} />
                </div>
              </div>
              <div className="border-t border-zinc-200 pt-5">
                <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500 font-chivo font-bold mb-4">Ligação (opcional)</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <SelectField label="Condomínio" testid="input-cond" value={form.condominio_id} onChange={(v) => setForm({ ...form, condominio_id: v })} options={condominios.map((c) => ({ value: c.id, label: c.nome }))} />
                  <SelectField label="Empresa de Condomínio" testid="input-emp" value={form.empresa_condominio_id} onChange={(v) => setForm({ ...form, empresa_condominio_id: v })} options={empresas.map((e) => ({ value: e.id, label: e.nome }))} />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold block mb-2">Descrição</label>
                <textarea
                  data-testid="input-descricao"
                  value={form.descricao || ""}
                  onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                  rows={3}
                  className="w-full bg-white border border-zinc-200 rounded-sm px-4 py-2.5 font-manrope text-sm focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none resize-none"
                />
              </div>
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-zinc-200">
                <button type="button" onClick={() => setOpenForm(false)} className="bg-white text-neutral-900 border border-zinc-200 hover:bg-zinc-50 rounded-sm px-5 py-2.5 font-manrope font-medium text-sm">Cancelar</button>
                <button type="submit" data-testid="save-btn" disabled={saving} className="bg-[#0047FF] text-white hover:bg-[#003BCC] rounded-sm px-6 py-2.5 font-manrope font-semibold text-sm disabled:opacity-60">
                  {saving ? "A guardar…" : editing ? "Guardar alterações" : "Registar consulta"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function EstadoChip({ s }) {
  const map = {
    Pendente: "bg-[#F59E0B]/10 text-[#B45309] border-[#F59E0B]/30",
    Realizada: "bg-[#16A34A]/10 text-[#15803D] border-[#16A34A]/30",
    Cancelada: "bg-zinc-100 text-neutral-600 border-zinc-200",
  };
  return (
    <span className={`inline-block text-[10px] font-chivo font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm border ${map[s] || map.Cancelada}`}>
      {s}
    </span>
  );
}

function TextField({ label, value, onChange, required, type = "text", testid, placeholder }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold block mb-2">{label}</label>
      <input data-testid={testid} type={type} required={required} value={value || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-zinc-200 rounded-sm px-4 py-2.5 font-manrope text-sm focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none" />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-chivo font-bold block mb-2">{label}</label>
      <select data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-zinc-200 rounded-sm px-4 py-2.5 font-manrope text-sm focus:ring-2 focus:ring-[#0047FF]/20 focus:border-[#0047FF] outline-none">
        <option value="">— Nenhum —</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
